import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, distinctUntilChanged, Observable } from 'rxjs';
import { Inspection, InspectionResolutionStatus } from '../inspection/inspection.dto';
import { Warehouse } from '../warehouse/warehouse.dto';
import { WarehouseInterceptorService } from '../warehouse/warehouse-interceptor.service';
import { CurrentWarehouseDataSource } from './current-warehouse-data-source';
import { HistoricalViewState } from './historical-view-state';
import { SnapshotSessionStore } from './snapshot-session.store';
import { SnapshotWarehouseDataSource } from './snapshot-warehouse-data-source';
import { WarehouseDataSource, WarehouseSourceRef } from './warehouse-data-source';

export const sameSourceRef = (a: WarehouseSourceRef, b: WarehouseSourceRef): boolean => a?.kind === b?.kind && a?.divisionId === b?.divisionId && a?.snapshotId === b?.snapshotId;

/**
 * Chooses between the live and the historical data source at runtime.
 *
 * Angular DI is static per injector, but this choice flips while the user is on the page. Resolving
 * it through a component provider or an InjectionToken factory would mean destroying and recreating
 * the canvas on every toggle — losing the decorator, the zoom state and the selected depth. So the
 * DI graph stays static and the selection lives here, as application state.
 *
 * The snapshot source and its payload store are registered only behind the feature flag, so
 * without it they resolve to null and selectSnapshot rejects.
 */
@Injectable()
export class WarehouseSourceService {
  private readonly current = inject(CurrentWarehouseDataSource);
  private readonly snapshot = inject(SnapshotWarehouseDataSource, { optional: true });
  private readonly store = inject(SnapshotSessionStore, { optional: true });
  private readonly warehouseInterceptorService = inject(WarehouseInterceptorService);
  private readonly historicalViewState = inject(HistoricalViewState);
  private selectionGeneration = 0;
  private inspectionSelectionGeneration = 0;
  private currentInspectionKey: string = null;
  private readonly inspectionCurrentStateOnlySubject = new BehaviorSubject<boolean>(false);

  private readonly refSubject = new BehaviorSubject<WarehouseSourceRef>({ kind: 'current', divisionId: null });

  readonly ref$: Observable<WarehouseSourceRef> = this.refSubject.pipe(distinctUntilChanged(sameSourceRef));

  /** True when the open inspection is completed without a snapshot, or with a layout-only one (known only once loaded). */
  readonly inspectionCurrentStateOnly$: Observable<boolean> = this.inspectionCurrentStateOnlySubject.pipe(distinctUntilChanged());

  constructor() {
    // A warehouse switch must never leave another division's payload selected.
    this.warehouseInterceptorService.$warehouse.subscribe((warehouse: Warehouse) => {
      if (warehouse?._id && warehouse._id !== this.refSubject.value.divisionId) {
        this.clearForDivision(warehouse._id);
      }
    });

    this.warehouseInterceptorService.$inspection.subscribe((inspection: Inspection) => {
      const divisionId = inspection?.division?._id;
      const inspectionId = inspection?._id;
      if (!divisionId || !inspectionId) {
        const wasOnInspection = !!this.currentInspectionKey;
        this.currentInspectionKey = null;
        this.inspectionSelectionGeneration++;
        this.inspectionCurrentStateOnlySubject.next(false);
        if (wasOnInspection && this.refSubject.value.divisionId) {
          this.selectCurrent(this.refSubject.value.divisionId);
        }
        return;
      }

      this.selectForInspection(inspection);
    });
  }

  ref(): WarehouseSourceRef {
    return this.refSubject.value;
  }

  isHistorical(): boolean {
    return this.refSubject.value.kind === 'snapshot';
  }

  /**
   * Whether this build can show historical state at all. False when the feature flag
   * (appConfig.watson.warehouseStateSnapshots) is off, because the snapshot source and its payload
   * store are only registered behind it. Check this before offering a snapshot picker.
   */
  isHistoricalAvailable(): boolean {
    return !!this.snapshot && !!this.store;
  }

  /**
   * Selects inspection.snapshotId without waiting for its payload. Returns inspectionCurrentStateOnly$'s
   * current value; subscribe to it to also catch layout-only snapshots.
   */
  selectForInspection(inspection: Inspection): boolean {
    const divisionId = inspection?.division?._id;
    const inspectionId = inspection?._id;
    if (!divisionId || !inspectionId) {
      return false;
    }

    // Snapshot id and completion in the key: a finished re-import switches to the new snapshot, and
    // completing or reopening the open inspection re-evaluates it.
    const completed = inspection.status?.status === InspectionResolutionStatus.RESOLVED;
    const inspectionKey = `${divisionId}:${inspectionId}:${inspection.snapshotId ?? ''}:${completed}`;
    if (inspectionKey === this.currentInspectionKey) {
      return this.inspectionCurrentStateOnlySubject.value;
    }

    this.currentInspectionKey = inspectionKey;
    this.inspectionCurrentStateOnlySubject.next(this.selectLinkedInspectionSnapshot(divisionId, inspection.snapshotId, completed, ++this.inspectionSelectionGeneration));
    return this.inspectionCurrentStateOnlySubject.value;
  }

  active(): WarehouseDataSource {
    return this.isHistorical() && this.snapshot ? this.snapshot : this.current;
  }

  selectCurrent(divisionId: string): void {
    // Flip only: no reload, no resync, and the current source's caches were never touched while
    // historical was active, so returning costs zero reads.
    this.selectionGeneration++;
    this.historicalViewState.deactivate();
    this.refSubject.next({ kind: 'current', divisionId });
  }

  async selectSnapshot(divisionId: string, snapshotId: string): Promise<void> {
    if (!this.isHistoricalAvailable()) {
      throw new Error('Historical warehouse states are not enabled in this environment.');
    }

    if (this.refSubject.value.divisionId && this.refSubject.value.divisionId !== divisionId) {
      return;
    }

    const generation = ++this.selectionGeneration;
    // Load before announcing, so triggered reads find it in flight and wait.
    const loading = this.store.load(divisionId, snapshotId);

    // Flip the read-through wrappers over with the canvas, so every other view on the page
    // (preview panels, grids, plan) reads the same point in time.
    this.historicalViewState.activate({ divisionId, snapshotId });
    this.refSubject.next({ kind: 'snapshot', divisionId, snapshotId });

    try {
      await loading;
    } catch (error) {
      // Back to live, unless the user moved on.
      if (generation === this.selectionGeneration) {
        this.selectCurrent(divisionId);
      }
      throw error;
    }
  }

  clearForDivision(divisionId: string): void {
    this.selectionGeneration++;
    this.historicalViewState.deactivate();
    this.store?.clear();
    this.refSubject.next({ kind: 'current', divisionId });
  }

  private selectLinkedInspectionSnapshot(divisionId: string, snapshotId: string | undefined, completed: boolean, generation: number): boolean {
    if (snapshotId && sameSourceRef(this.refSubject.value, { kind: 'snapshot', divisionId, snapshotId: String(snapshotId) })) {
      // Already selected: keep it, but still check layout-only.
      this.store
        .whenLoaded({ divisionId, snapshotId: String(snapshotId) })
        .then(() => this.leaveLayoutOnlySnapshot(divisionId, String(snapshotId), generation))
        .catch(console.error);
      return false;
    }

    // Never leave a snapshot selected for a different inspection showing.
    this.selectCurrent(divisionId);
    if (!this.isHistoricalAvailable()) {
      return false;
    }

    if (!snapshotId) {
      // The current state is what an inspection still in progress is about, so only a completed one
      // misses its snapshot: it predates them, or its snapshot isn't built or downloaded yet.
      return completed;
    }

    this.selectSnapshot(divisionId, String(snapshotId))
      .then(() => this.leaveLayoutOnlySnapshot(divisionId, String(snapshotId), generation))
      // Failure must not block the inspection; selectSnapshot already went live.
      .catch((error: unknown) => console.warn(`Could not load the snapshot linked to this inspection: ${snapshotId}.`, error));

    return false;
  }

  /** Layout-only snapshots have no findings; show live instead. */
  private leaveLayoutOnlySnapshot(divisionId: string, snapshotId: string, generation: number): void {
    const stillSelected = generation === this.inspectionSelectionGeneration && this.refSubject.value.snapshotId === snapshotId;
    if (stillSelected && this.store.isLoaded({ divisionId, snapshotId }) && this.store.metaFor(divisionId)?.layoutOnly) {
      this.selectCurrent(divisionId);
      this.inspectionCurrentStateOnlySubject.next(true);
    }
  }
}
